import type { BlockModel } from '@/lib/ooxml';

function toRoman(n: number): string {
  const table: [number, string][] = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
  ];
  let out = '';
  let rest = n;
  for (const [value, glyph] of table) {
    while (rest >= value) {
      out += glyph;
      rest -= value;
    }
  }
  return out;
}

function toLetter(n: number): string {
  let out = '';
  let rest = n;
  while (rest > 0) {
    rest -= 1;
    out = String.fromCharCode(97 + (rest % 26)) + out;
    rest = Math.floor(rest / 26);
  }
  return out;
}

function formatMarker(fmt: string | null, n: number): string {
  switch (fmt) {
    case 'decimal':
      return `${n}.`;
    case 'lowerLetter':
      return `${toLetter(n)}.`;
    case 'upperLetter':
      return `${toLetter(n).toUpperCase()}.`;
    case 'lowerRoman':
      return `${toRoman(n)}.`;
    case 'upperRoman':
      return `${toRoman(n).toUpperCase()}.`;
    case 'none':
      return '';
    case 'bullet':
    case null:
    default:
      return '•';
  }
}

/**
 * Compute display markers for list items in one pass over the blocks.
 *
 * Counters are kept per (numId, level); incrementing a level resets the
 * deeper levels of the same numbering instance — the common continuous
 * cases render exactly, exotic restart overrides degrade to a best-effort
 * count (charter: bounded fallback, the PDF tab is the fidelity view).
 */
export function computeListMarkers(blocks: BlockModel[]): Map<number, string> {
  const counters = new Map<string, number>();
  const markers = new Map<number, string>();

  for (const block of blocks) {
    if (block.kind !== 'list_item') {
      continue;
    }
    const numId = block.numId ?? '?';
    const level = block.level ?? 0;
    if (block.listFormat === 'bullet' || block.listFormat === null) {
      markers.set(block.pos, formatMarker(block.listFormat, 0));
      continue;
    }
    const key = `${numId}/${level}`;
    const next = (counters.get(key) ?? 0) + 1;
    counters.set(key, next);
    for (const existing of Array.from(counters.keys())) {
      const [id, lvl] = existing.split('/');
      if (id === numId && Number(lvl) > level) {
        counters.delete(existing);
      }
    }
    markers.set(block.pos, formatMarker(block.listFormat, next));
  }

  return markers;
}
